import { CheckoutContext } from "contexts/Checkout";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { AddOnItemName } from "schemas/dashboard";

import { useCheckout } from "components/StripeCheckout/useCheckout";

import AddOnCard from "./AddOnCard";

const UnitName = {
  [AddOnItemName.pitching_templates]: "template",
  [AddOnItemName.support_call]: "call",
  [AddOnItemName.email_volume_monthly]: "package",
  [AddOnItemName.email_volume_yearly]: "package",
  [AddOnItemName.email_volume_pro_monthly]: "package",
  [AddOnItemName.email_volume_pro_yearly]: "package",
};

interface Props {
  addOnItemName: AddOnItemName;
}

const EmailAddOn = ({ addOnItemName }: Props) => {
  const { applyAddOnLoading, addOnItems } = useContext(CheckoutContext);
  const { addOns } = useContext(SubscriptionContext);
  const { applyAddOnItem, canPurchaseMoreEmailVolume, isAtMaxEmailVolume } =
    useCheckout();
  const unit = UnitName[addOnItemName as keyof typeof UnitName];
  const quantity =
    addOnItems?.find((x) => x.name === addOnItemName)?.quantity || 0;
  const [error, setError] = useState<string>("");

  const emailVolumeNames = [
    AddOnItemName.email_volume_monthly,
    AddOnItemName.email_volume_yearly,
    AddOnItemName.email_volume_pro_monthly,
    AddOnItemName.email_volume_pro_yearly,
  ];
  const isEmailVolume = emailVolumeNames.includes(addOnItemName);

  const handleAddToOrder = () => {
    if (isEmailVolume) {
      if (!validateEmailVolumes()) return;
    } else {
      const maxQuantity = addOns[addOnItemName]?.max_quantity;
      if (quantity >= maxQuantity) {
        setError(
          `This item is limit to ${addOns[addOnItemName]?.max_quantity} per order.`,
        );
        return;
      }
    }

    setError("");
    applyAddOnItem({
      name: addOnItemName,
      quantity: increaseQuantity(),
    });
  };

  const handleDecrease = () => {
    setError("");
    applyAddOnItem({
      name: addOnItemName,
      quantity: Math.max(0, quantity - 1),
    });
  };

  const validateEmailVolumes = () => {
    if (!isEmailVolume) return true;
    let currCount = 0;
    const currentEmailVolumes = addOnItems?.filter((item) =>
      emailVolumeNames.includes(item.name),
    );
    if (currentEmailVolumes && currentEmailVolumes.length > 0) {
      currentEmailVolumes.forEach((item) => {
        currCount += item.quantity;
      });
    }
    const canPurchaseMore = canPurchaseMoreEmailVolume(
      addOnItemName,
      currCount + 1,
    );
    if (!canPurchaseMore) {
      setError(
        `You have reached the maximum number of ${addOns[addOnItemName]?.max_quantity} active email volume packages.`,
      );
    }
    return canPurchaseMore;
  };

  const increaseQuantity = () => {
    let currQuantity: number = quantity;
    setError("");
    return currQuantity + 1;
  };

  const renderPrice = () => {
    if (
      addOnItemName === AddOnItemName.email_volume_monthly ||
      addOnItemName === AddOnItemName.email_volume_pro_monthly
    ) {
      return "mo";
    } else if (
      addOnItemName === AddOnItemName.email_volume_yearly ||
      addOnItemName === AddOnItemName.email_volume_pro_yearly
    ) {
      return "yr";
    }
    return unit;
  };

  const maxQuantity = addOns[addOnItemName]?.max_quantity;
  const hasReachedMaxQuantity = quantity >= maxQuantity;
  useEffect(() => {
    if (error && !hasReachedMaxQuantity) {
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasReachedMaxQuantity]);

  if (isAtMaxEmailVolume()) {
    return null;
  }

  return (
    <AddOnCard
      icon="fa-regular fa-envelope-open"
      title={addOns[addOnItemName]?.display_name}
      priceText={`$${addOns[addOnItemName]?.price}.00`}
      per={`/${renderPrice()}`}
      tag="Bundle"
      limitNote={maxQuantity ? `Up to ${maxQuantity}` : undefined}
      description={addOns[addOnItemName]?.description}
      loading={applyAddOnLoading === addOnItemName}
      error={error}
      quantity={quantity}
      maxQuantity={maxQuantity}
      canIncrement={!hasReachedMaxQuantity}
      onAdd={handleAddToOrder}
      onRemove={handleDecrease}
    />
  );
};

export default EmailAddOn;
